import {
    readReferralCode,
    readReferralEntryId,
} from '@common/referral/referralAttribution';
import type { VerifyAuthResponse } from '@common/api/authService';
import { getEmailOtpPostSignInRedirect } from './emailOtpAuth';

export type ReferralSignInState = 'idle' | 'checking' | 'redirecting' | 'error';
export type ReferralResolutionStatus = 'not_applicable' | 'loading' | 'accepted' | 'invalid';

/** AuthPage owns this request. Cleanup invalidates both success and failure results. */
export const startReferralSignInRouting = ({
    search,
    loadProfile,
    onStateChange,
    navigate,
    referralResolutionStatus,
}: {
    search: string;
    loadProfile: () => Promise<Pick<VerifyAuthResponse, 'workspace'>>;
    onStateChange: (state: ReferralSignInState) => void;
    navigate: (destination: string) => void;
    referralResolutionStatus: ReferralResolutionStatus;
}): (() => void) => {
    let active = true;
    if (referralResolutionStatus !== 'accepted') {
        return () => { active = false; };
    }
    onStateChange('checking');
    void Promise.resolve().then(loadProfile).then((profile) => {
        if (!active) return;
        const destination = getEmailOtpPostSignInRedirect(search, profile);
        if (!destination) throw new Error('Account destination is not ready.');
        onStateChange('redirecting');
        navigate(destination);
    }).catch(() => {
        if (active) onStateChange('error');
    });
    return () => { active = false; };
};

export const REFERRAL_AUTH_PREVIEW_QUERY_PARAM = 'referral_preview';

export const isReferralAuthPreview = (search: string): boolean => (
    new URLSearchParams(search).get(REFERRAL_AUTH_PREVIEW_QUERY_PARAM) === '1'
);

const createReferralEntryId = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    return `entry_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

export const buildReferralAuthSearch = (
    search: string,
    origin: string,
    createEntryId: () => string = createReferralEntryId,
): string => {
    const referralCode = readReferralCode(search);
    if (!referralCode) {
        return search;
    }

    const params = new URLSearchParams(search);
    const referralEntryId = readReferralEntryId(search) || createEntryId();
    const onboardingUrl = new URL('/admin/onboarding', origin);
    onboardingUrl.searchParams.set('ref', referralCode);
    onboardingUrl.searchParams.set('referral_entry', referralEntryId);

    params.set('ref', referralCode);
    params.set('referral_entry', referralEntryId);
    // Keep the onboarding return for workspace-less accounts and new signups.
    // Post-sign-in routing checks the profile before consuming this destination.
    params.set('redirect', onboardingUrl.toString());

    return `?${params.toString()}`;
};

export const buildAuthReturnUrl = (
    origin: string,
    authEntryPath: string,
    search: string,
): string => `${origin}${authEntryPath}${search}`;
